import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./questionnaires.module.css";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  useAddOrEditQuestionnaireMutation,
  useDeleteQuestionnaireMutation,
  useGetQuestionnairesMutation,
} from "../../services/api/utils.api";
import { useGetAiFeaturesForSelectQuery } from "../../services/api/ai.api";
import { useAppSelector } from "../../services/hooks";
import { questionnairesSelector } from "../../services/selectors/utils.selectors";
import Loader from "../../components/loader/loader";
import { useNavigate, useSearchParams } from "react-router-dom";
import Modal from "../../components/modals/modal/Modal";
import ErrorPopup from "../../components/modals/errorPopup/errorPopup";
import DeleteConfirmation from "../../components/modals/deleteConfirmation/deleteConfirmation";
import useMediaQuery from "../../hooks/useMediaQuery";
import { adminSelector } from "../../services/selectors/user.selectors";
import { IQuestionnaireData } from "../../services/types/utils.types";
import { filterObjectsByString } from "../../utils/search";
import { useActions } from "../../services/hooks/useActions";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import ListPagination from "../../components/listPagination/listPagination";
import GeneralSuccessPopup from "../../components/modals/generalSuccessPopup/generalSuccessPopup";
import ActionButtonTooltip from "../../components/ActionButtonTooltip/actionButtonTooltip";
import { currentQuestionnaireSelector } from "../../services/selectors/users.selectors";
import ExportTableButtons from "../../components/exportTableButtons/ExportTableButtons";

type QuestionnaireTab = "all" | "favorites";

const isQuestionnaireFavorite = (questionnaire: IQuestionnaireData) =>
  questionnaire.isFavorite === 1;

function QuestionnairesPage() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page");
  const pageAsNumber = Number(page);

  const mobile = useMediaQuery("(max-width: 800px)");
  const questionnairesServer = useAppSelector(questionnairesSelector);
  const admin = useAppSelector(adminSelector);
  const limitNumber = 8;

  const { resetQuestions } = useActions();

  const [inputValue, setInputValue] = useState<string>("");
  const [activeTab, setActiveTab] = useState<QuestionnaireTab>("all");
  const [openModal, setOpenModal] = useState(false);
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState<
    IQuestionnaireData | undefined
  >(undefined);
  const [questionnaires, setQuestionnaires] = useState<IQuestionnaireData[]>([]);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [togglingFavoriteId, setTogglingFavoriteId] = useState<number | null>(
    null
  );

  const [getQuestionnaires, { isLoading }] = useGetQuestionnairesMutation();
  const [deleteQuestionnaire] = useDeleteQuestionnaireMutation();
  const [editQuestionnaire, { isLoading: isUpdatingFavorite }] =
    useAddOrEditQuestionnaireMutation();
  const [limits, setLimits] = useState(
    pageAsNumber
      ? {
          start: (pageAsNumber - 1) * limitNumber + 1,
          end: pageAsNumber * limitNumber,
        }
      : { start: 1, end: limitNumber }
  );

  const paginatedQuestionnaires = questionnaires.slice(
    limits.start - 1,
    limits.end
  );

  const [shouldResetPagination, setShouldResetPagination] =
    useState<boolean>(false);
  const { data: aiFeatures = [] } = useGetAiFeaturesForSelectQuery();

  const favoriteCount = useMemo(
    () => questionnairesServer.filter(isQuestionnaireFavorite).length,
    [questionnairesServer]
  );

  const emptyMessage =
    activeTab === "favorites" && !inputValue
      ? t("NO_FAVORITE_QUESTIONNAIRES")
      : t("NO_RESULTS_FOUND");

  useEffect(() => {
    if (
      pageAsNumber * limitNumber > questionnaires.length &&
      questionnaires.length > 0
    )
      setLimits({
        start:
          Math.floor((questionnaires.length - 1) / limitNumber) * limitNumber +
          1,
        end: questionnaires.length,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaires]);

  useEffect(() => {
    getQuestionnaires();
  }, [getQuestionnaires, admin.companyId]);

  useEffect(() => {
    resetQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnairesServer, resetQuestions]);

  useEffect(() => {
    const tabFiltered =
      activeTab === "favorites"
        ? questionnairesServer.filter(isQuestionnaireFavorite)
        : questionnairesServer;

    setQuestionnaires(filterObjectsByString(inputValue, tabFiltered));
    if (shouldResetPagination) {
      setLimits({ start: 1, end: limitNumber });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnairesServer, inputValue, activeTab]);

  useEffect(() => {
    setSearchParams(
      { page: (Math.floor(limits.start / limitNumber) + 1).toString() },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limits]);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShouldResetPagination(true);
    setInputValue(e.target.value);
  };

  const handleTabChange = (tab: QuestionnaireTab) => {
    setShouldResetPagination(true);
    setActiveTab(tab);
  };

  const handleToggleFavorite = (questionnaire: IQuestionnaireData) => {
    setTogglingFavoriteId(questionnaire.id);
    editQuestionnaire({
      body: {
        ...questionnaire,
        isFavorite: isQuestionnaireFavorite(questionnaire) ? 0 : 1,
      },
    })
      .then((res) => {
        if ("error" in res) setOpenModalError(true);
      })
      .catch(() => setOpenModalError(true))
      .finally(() => setTogglingFavoriteId(null));
  };

  const handleDelete = () => {
    if (!questionnaireToDelete) return;
    deleteQuestionnaire({
      questionnaire: questionnaireToDelete,
    })
      .then((res) => {
        if ("data" in res && res.data) setOpenModalSuccess(true);
        else if ("error" in res) setOpenModalError(true);
      })
      .catch(() => setOpenModalError(true));
  };

  const { setCurrentQuestionnaire } = useActions();
  const selectQuestionnaire = (id: number) => () => {
    setCurrentQuestionnaire(id);
  };
  const currentQuestionnaire = useAppSelector(currentQuestionnaireSelector);

  return (
    <section className="questionnaires">
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={() => navigate(-1)}
        position={"right"}
      />
      <h1 className="title title_blue">{t("ALL_QUESTIONNAIRES")}</h1>
      <div className="header" style={{ gap: "10px" }}>
        <button
          type={"button"}
          className="addBtn"
          onClick={() => {
            resetQuestions();
            navigate("edit");
          }}
        >
          {!mobile && t("ADD_NEW_QUESTIONNAIRE_1")}
          <span className="plus">+</span>
        </button>

        <input
          type={"text"}
          placeholder={t("FILTERING_1")}
          className="searchInput"
          onChange={onInputChange}
          value={inputValue}
        />
      </div>

      <div className={styles.tabsContainer}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "all" ? styles.tabActive : ""}`}
          onClick={() => handleTabChange("all")}
        >
          {t("ALL_QUESTIONNAIRES")}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === "favorites" ? styles.tabActive : ""}`}
          onClick={() => handleTabChange("favorites")}
        >
          {t("FAVORITE_QUESTIONNAIRES")}
          {favoriteCount > 0 && (
            <span className={styles.tabBadge}>{favoriteCount}</span>
          )}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          width: "97%",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <ExportTableButtons
          entity="Questionnaire"
          filters={{ CompanyId: admin.companyId }}
        />
      </div>
      {isLoading ? (
        <Loader />
      ) : questionnaires.length > 0 ? (
        <article className="section">
          <h3 className="sectionTitle">{t("QUESTIONNAIRES")}</h3>
          <ul className={`list ${styles.list}`}>
            <li
              className={`listRow ${styles.listRow} listRow_header ${styles.listRow_header}`}
            >
              <p className={`listCell ${styles.idCell}`}>
                <span className={styles.idCellSpacer} aria-hidden="true" />
                {t("NUMBER_1")}
              </p>
              <p className="listCell">{t("NAME_2")}</p>
              <p className="listCell">{t("AI_QUESTIONNAIRE")}</p>
              <p className="listCell">{t("AMOUNT_OF_AI_QUESTIONS")}</p>
              <p className="listCell">{t("SIGN_1")}</p>
              <p className="listCell">{t("RRM_WEIGHT")}</p>
              <p className="listCell">{t("COLUMN_STATUS")}</p>
              <p className="listCell">{t("COLUMN_OPTIONS")}</p>
            </li>
            {paginatedQuestionnaires.map((q, i) => {
              const index = i + (limits.start - 1);
              const feature = aiFeatures.find(
                (f) => f.value === q.chatGPTFeaturesId?.toString()
              );
              const featureName = feature
                ? feature.label === "WITHOUT_FEATURE"
                  ? ""
                  : feature.label
                : "";
              const isFavorite = isQuestionnaireFavorite(q);
              const isFavoriteLoading =
                isUpdatingFavorite && togglingFavoriteId === q.id;

              return (
                <li
                  key={q.id}
                  className={`listRow ${styles.listRow} ${
                    index % 2 === 0 && "listRow_colored"
                  }
                  ${q.id === currentQuestionnaire ? "listRow_selected" : ""}`}
                  onClick={selectQuestionnaire(q.id)}
                >
                  <p className={`listCell ${styles.idCell}`}>
                    <span
                      className={styles.favoriteCell}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isFavoriteLoading ? (
                        <span className={styles.favoriteLoader}>
                          <Loader />
                        </span>
                      ) : (
                        <ActionButtonTooltip
                          className={`${styles.favoriteBtn} ${
                            isFavorite ? styles.favoriteBtn_active : ""
                          }`}
                          onClick={() => handleToggleFavorite(q)}
                          text={
                            isFavorite
                              ? t("REMOVE_FROM_FAVORITES")
                              : t("ADD_TO_FAVORITES")
                          }
                          tooltipPosition="left"
                        >
                          <span className={styles.favoriteStar}>
                            {isFavorite ? "★" : "☆"}
                          </span>
                        </ActionButtonTooltip>
                      )}
                    </span>
                    <span className={styles.idValue}>{q.id}</span>
                  </p>
                  <p className={`listCell ${styles.nameText}`}>{q.desc}</p>
                  <p className="listCell">{featureName}</p>
                  <p className="listCell">
                    {q.aiQuestionsLimit || t("WITHOUT_LIMITATION")}
                  </p>
                  <p className="listCell">{q.symbol}</p>
                  <p className="listCell">{q.factorQuestionnaire}</p>
                  <p className="listCell">
                    {q.statusId === 1 ? (
                      t("STATUS_ACTIVE")
                    ) : (
                      <span className="status_inactive">
                        {t("STATUS_INACTIVE")}
                      </span>
                    )}
                  </p>

                  <div className={`listCell ${styles.options}`}>
                    <ActionButtonTooltip
                      className="btn btn_type_edit"
                      onClick={() =>
                        navigate(`/protocols/questionnaires/edit/${q.id}`)
                      }
                      text={t("EDIT_QUESTIONNAIRE_1")}
                    />
                    <ActionButtonTooltip
                      className="btn btn_type_tree"
                      onClick={() =>
                        navigate(`/protocols/questionnaires/map-test/${q.id}`)
                      }
                      text={t("SHOW")}
                    />
                    <ActionButtonTooltip
                      className="btn btn_type_rrm"
                      onClick={() => {
                        navigate(`edit/${q.id}/rrm`);
                      }}
                      text={t("EDIT_RRM_GROUPS")}
                      tooltipPosition="left"
                    />
                    <ActionButtonTooltip
                      className="btn btn_type_copy"
                      onClick={() => {
                        navigate(`/protocols/questionnaires/edit/${q.id}/copy`);
                      }}
                      text={t("COPY_QUESTIONNAIRE")}
                      tooltipPosition="left"
                    />
                    <ActionButtonTooltip
                      className={`btn btn_type_delete ${
                        q.statusId === 0
                          ? "btn_type_delete_disabled button_disabled"
                          : ""
                      }`}
                      onClick={() => {
                        if (q.statusId === 1) {
                          setQuestionnaireToDelete(q);
                          setOpenModal(true);
                        }
                      }}
                      text={`${
                        q.statusId === 0
                          ? t("QUESTIONNIARE_IS_DISABLED")
                          : t("DISABLE_QUESTIONNAIRE")
                      }`}
                      tooltipPosition="left"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
          <ListPagination
            total={questionnaires.length}
            limits={limits}
            setLimits={setLimits}
            step={8}
          />
        </article>
      ) : (
        <p className="text">{emptyMessage}</p>
      )}
      {openModal && questionnaireToDelete && (
        <Modal onClose={() => setOpenModal(false)}>
          <DeleteConfirmation
            onClose={() => setOpenModal(false)}
            onDelete={handleDelete}
            text={t("CONF_CHANGE_QUESTIONNAIRE_STATUS", {
              desc: questionnaireToDelete?.desc,
            })}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
    </section>
  );
}

export default QuestionnairesPage;
